import { useState, useCallback, useRef, useEffect } from 'react';
import CastellerRow from './CastellerRow';
import PhotoModal from './PhotoModal';
import { hasAlturaMansFeature } from '../../../utils/colla-features';

function CastellersTable({
    users,
    updateUser,
    deleteUser,
    show_popup,
    userInfo,
    COLLA,
    socket,
    userId,
    sortBy,
    sortDirection,
    onSort,
    showEtiquetes,
    allEtiquetes,
    userEtiquetes,
    onEtiquetaToggle
}) {
    // Guardem tant l'userId com la imatge ja carregada
    const [photoModal, setPhotoModal] = useState(null);
    const [photoModalImage, setPhotoModalImage] = useState(null);
    const [profilePicOverrides, setProfilePicOverrides] = useState({});

    // Refs per evitar stale closures en callbacks amb debounce
    // Això assegura que sempre tenim accés a les dades més recents
    const usersRef = useRef(users);
    const updateUserRef = useRef(updateUser);

    useEffect(() => {
        usersRef.current = users;
    }, [users]);

    useEffect(() => {
        updateUserRef.current = updateUser;
    }, [updateUser]);

    // Component per capçaleres ordenables
    const SortableHeader = ({ field, children }) => {
        const isActive = sortBy === field;
        const arrow = !isActive ? '' : sortDirection === 'asc' ? ' ↑' : ' ↓';

        return (
            <th
                className={`sortable-header ${isActive ? 'active' : ''}`}
                onClick={() => onSort(field)}
            >
                {children}{arrow}
            </th>
        );
    };

    // Handler per canvis de camps - utilitza refs per evitar stale closures
    // Quan el debounce de CastellerRow dispara després de 500ms, sempre llegeix
    // les dades més recents dels refs en comptes de closures potencialment obsoletes
    const handleFieldChange = useCallback((userIdToUpdate, field, value) => {
        const currentUsers = usersRef.current;
        const currentUpdateUser = updateUserRef.current;
        
        const user = currentUsers.find(u => u.id === userIdToUpdate);
        if (!user) return;

        const changes = {};
        changes[field] = value;
        currentUpdateUser(user, changes);
    }, []);


    return (
        <div className="castellers-table-container">
            <table className="castellers-table">
                <thead>
                    <tr>
                        <th>Foto</th>
                        <SortableHeader field="mote">Sobrenom</SortableHeader>
                        <SortableHeader field="nom">Nom</SortableHeader>
                        <SortableHeader field="primer-cognom">1r Cognom</SortableHeader>
                        <SortableHeader field="segon-cognom">2n Cognom</SortableHeader>
                        <SortableHeader field="altura">Altura</SortableHeader>
                        {hasAlturaMansFeature() ? (
                            <SortableHeader field="altura_mans">Alt. mans</SortableHeader>
                        ) : null}
                        <SortableHeader field="es_tecnica">Tècnica</SortableHeader>
                        <SortableHeader field="es_junta">Junta</SortableHeader>
                        {showEtiquetes && allEtiquetes?.map(etiqueta => {
                            const field = `etiqueta_${etiqueta.id}`;
                            const isActive = sortBy === field;
                            const arrow = !isActive ? '' : sortDirection === 'asc' ? ' ↑' : ' ↓';
                            return (
                                <th 
                                    key={etiqueta.id} 
                                    className={`etiqueta-header sortable-header ${isActive ? 'active' : ''}`}
                                    title={etiqueta.nom}
                                    onClick={() => onSort(field)}
                                >
                                    {etiqueta.nom}{arrow}
                                </th>
                            );
                        })}
                        <SortableHeader field="canalla">Canalla</SortableHeader>
                        <SortableHeader field="music">Músic</SortableHeader>
                        <SortableHeader field="lesionat">Lesionat</SortableHeader>
                        <SortableHeader field="novell">Novell</SortableHeader>
                        <SortableHeader field="soci">Soci</SortableHeader>
                        <SortableHeader field="casteller">Casteller</SortableHeader>
                        <th>Accions</th>
                    </tr>
                </thead>
                <tbody>
                    {users.map(user => (
                        <CastellerRow
                            key={user.id}
                            user={user}
                            onFieldChange={handleFieldChange}
                            deleteUser={deleteUser}
                            onPhotoClick={(loadedImage) => {
                                setPhotoModal(user.id);
                                setPhotoModalImage(loadedImage);
                            }}
                            profilePicOverride={profilePicOverrides[user.id]}
                            show_popup={show_popup}
                            userInfo={userInfo}
                            COLLA={COLLA}
                            socket={socket}
                            sortBy={sortBy}
                            showEtiquetes={showEtiquetes}
                            allEtiquetes={allEtiquetes}
                            userEtiquetes={userEtiquetes?.[user.id] || []}
                            onEtiquetaToggle={onEtiquetaToggle}
                        />
                    ))}
                </tbody>
            </table>

            {photoModal ? (
                <PhotoModal
                    userId={photoModal}
                    onClose={() => {
                        setPhotoModal(null);
                        setPhotoModalImage(null);
                    }}
                    COLLA={COLLA}
                    initialImage={photoModalImage}
                    onPhotoUpdated={(newImage) => {
                        setPhotoModalImage(newImage);
                        setProfilePicOverrides(prev => ({
                            ...prev,
                            [photoModal]: newImage
                        }));
                    }}
                />
            ) : null}
        </div>
    );
}

export default CastellersTable;
